import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router';
import AppToaster from './components/AppToaster';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { TenantThemeProvider } from './contexts/TenantThemeContext';
import TenantManifestLink from './components/TenantManifestLink';
import InstallPrompt from './components/InstallPrompt';

import AuthPage from './pages/auth/AuthPage';
import AuthCallback from './pages/auth/AuthCallback';
import PlayerApp from './pages/player/PlayerApp';
import AndroidAppDownloadPage from './pages/player/AndroidAppDownloadPage';
import ExploreEarnPage from './pages/player/ExploreEarnPage';
import PromoCodePage from './pages/player/PromoCodePage';
import VipRewardsPage from './pages/player/VipRewardsPage';
import ExploreSubPage from './pages/player/ExploreSubPage';
import RoulettePage from './pages/player/RoulettePage';
import ReferralPage from './pages/player/ReferralPage';
import BrandProfilePage from './pages/player/BrandProfilePage';
import TasksPage from './pages/player/TasksPage';
import HalloweenEventPage from './pages/player/components/HalloweenEventPage';
import DepositReturnPage from './pages/player/DepositReturnPage';
import AdminLayout from './pages/admin/AdminLayout';
// TenantAuthPage retired — `/<slug>/auth` now uses the shared AuthPage,
// which is tenant-aware via `useTenantTheme()` + `X-Tenant-Slug` header.
import LandingPage from './pages/LandingPage';
import StaffPortalPage from './pages/staff/StaffPortalPage';
import FinancePortalPage from './pages/finance/FinancePortalPage';
import { tenantSlugFromHost } from './utils/tenantDomains';

const RESERVED_PATHS = ['auth','app','admin','api','staff','staff-portal','finance','survey','deposit-return','download-app'];
const ADMIN_PORTAL_ROLES = [
  'admin',
  'tenant_admin',
  'moderator',
  'support',
  'staff',
  'support_agent',
  'finance_staff',
  'survey_staff',
  'agent',
  'affiliate_manager',
  'referral_agent',
];

function getTenantHostSlug() {
  return tenantSlugFromHost();
}

function ProtectedRoute({ children, requiredRole, playerOnly = false }) {
  const { user, loading, tenantSlug } = useAuth();
  const location = useLocation();
  const hostTenantSlug = getTenantHostSlug();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B0E14' }}>
        <div className="spinner" />
      </div>
    );
  }

  const pathMatch = location.pathname.match(/^\/([a-z0-9-]+)\//i);
  const slug = tenantSlug || hostTenantSlug || (pathMatch && !RESERVED_PATHS.includes(pathMatch[1]) ? pathMatch[1] : null);
  const routePrefix = hostTenantSlug ? '' : (slug ? `/${slug}` : '');

  if (!user) {
    if (requiredRole === 'admin') {
      const isChatRoute = /\/admin\/chat(\/|$)/.test(location.pathname);
      const params = new URLSearchParams();
      params.set('next', location.pathname + location.search);
      if (isChatRoute) {
        params.set('staff_chat', '1');
      }
      const suffix = params.toString() ? `?${params.toString()}` : '';
      return <Navigate to={`${routePrefix}/admin/auth${suffix}`} replace />;
    }
    return <Navigate to={`${routePrefix}/auth`} replace />;
  }

  const userTenantSlug = user.tenant_slug || user.tenant?.slug || null;
  if (userTenantSlug && !user.is_master_admin && !hostTenantSlug) {
    const requestedSlug = pathMatch && !RESERVED_PATHS.includes(pathMatch[1]) ? pathMatch[1] : null;
    if (requestedSlug && requestedSlug !== userTenantSlug) {
      const suffix = location.pathname.replace(/^\/[^/]+/, '') || (ADMIN_PORTAL_ROLES.includes(user.role) ? '/admin' : '/app');
      return <Navigate to={`/${userTenantSlug}${suffix}`} replace />;
    }
    if (!requestedSlug && (location.pathname.startsWith('/admin') || location.pathname.startsWith('/app'))) {
      return <Navigate to={`/${userTenantSlug}${location.pathname}`} replace />;
    }
  }

  if (requiredRole === 'admin' && !ADMIN_PORTAL_ROLES.includes(user.role)) {
    return <Navigate to={`${routePrefix}/app`} replace />;
  }

  if (playerOnly && ADMIN_PORTAL_ROLES.includes(user.role)) {
    return <Navigate to={`${routePrefix}/admin`} replace />;
  }

  return children;
}

// Handles bare tenant URL like /morpheus → show branded LandingPage
// (if user already logged in, redirect to their app/admin)
function TenantRootRedirect({ tenantSlugOverride = null }) {
  const params = useParams();
  const tenantSlug = tenantSlugOverride || params.tenantSlug;
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B0E14' }}>
        <div className="spinner" />
      </div>
    );
  }

  if (user) {
    if (tenantSlugOverride) {
      if (ADMIN_PORTAL_ROLES.includes(user.role)) {
        return <Navigate to="/admin" replace />;
      }
      return <Navigate to="/app" replace />;
    }
    if (ADMIN_PORTAL_ROLES.includes(user.role)) {
      return <Navigate to={`/${tenantSlug}/admin`} replace />;
    }
    return <Navigate to={`/${tenantSlug}/app`} replace />;
  }

  // Unauthenticated → show branded landing page (main landing w/ tenant theme)
  return <LandingPage tenantSlugOverride={tenantSlugOverride ? tenantSlug : null} />;
}

function RootEntry() {
  const hostTenant = getTenantHostSlug();
  if (hostTenant) return <TenantRootRedirect tenantSlugOverride={hostTenant} />;
  return <Navigate to="/admin/auth" replace />;
}

function RootAuthEntry() {
  return getTenantHostSlug()
    ? <AuthPage portal="player" />
    : <Navigate to="/admin/auth" replace />;
}

function RootAppEntry({ children }) {
  return getTenantHostSlug()
    ? <ProtectedRoute playerOnly>{children}</ProtectedRoute>
    : <Navigate to="/admin" replace />;
}

function PlayerHomeRedirect() {
  const { tenantSlug } = useParams();
  return <Navigate to={tenantSlug ? `/${tenantSlug}/app` : '/app'} replace />;
}

function AppRouter() {
  const location = useLocation();

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Master routes */}
      <Route path="/auth" element={<RootAuthEntry />} />
      <Route path="/admin/auth" element={<AuthPage portal="admin" />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/deposit-return" element={<DepositReturnPage />} />
      <Route path="/download-app" element={<AndroidAppDownloadPage />} />
      <Route path="/app" element={<RootAppEntry><PlayerApp /></RootAppEntry>} />
      <Route path="/app/fifa-kit" element={<PlayerHomeRedirect />} />
      <Route path="/app/explore" element={<RootAppEntry><ExploreEarnPage /></RootAppEntry>} />
      <Route path="/app/explore/promo-code" element={<RootAppEntry><PromoCodePage /></RootAppEntry>} />
      <Route path="/app/explore/vip-rewards" element={<RootAppEntry><VipRewardsPage /></RootAppEntry>} />
      <Route path="/app/explore/marketplace" element={<RootAppEntry><ExploreSubPage section="marketplace" /></RootAppEntry>} />
      <Route path="/app/explore/tiers" element={<RootAppEntry><ExploreSubPage section="tiers" /></RootAppEntry>} />
      <Route path="/app/explore/daily-tasks" element={<RootAppEntry><TasksPage kind="daily" /></RootAppEntry>} />
      <Route path="/app/explore/weekly-tasks" element={<RootAppEntry><TasksPage kind="weekly" /></RootAppEntry>} />
      <Route path="/app/explore/referral" element={<RootAppEntry><ReferralPage /></RootAppEntry>} />
      <Route path="/app/explore/roulette" element={<RootAppEntry><RoulettePage /></RootAppEntry>} />
      <Route path="/app/halloween" element={<RootAppEntry><HalloweenEventPage /></RootAppEntry>} />
      <Route path="/app/explore/predict-score" element={<PlayerHomeRedirect />} />
      <Route path="/app/brand" element={<RootAppEntry><BrandProfilePage /></RootAppEntry>} />
      <Route path="/app/*" element={<RootAppEntry><PlayerApp /></RootAppEntry>} />
      <Route path="/admin" element={<ProtectedRoute requiredRole="admin"><AdminLayout /></ProtectedRoute>} />
      <Route path="/admin/*" element={<ProtectedRoute requiredRole="admin"><AdminLayout /></ProtectedRoute>} />
      <Route path="/staff-portal" element={<ProtectedRoute><StaffPortalPage /></ProtectedRoute>} />
      <Route path="/finance" element={<FinancePortalPage />} />
      <Route path="/finance/auth" element={<FinancePortalPage />} />
      <Route path="/survey/auth" element={<AuthPage portal="admin" />} />
      <Route path="/survey" element={<ProtectedRoute requiredRole="admin"><AdminLayout /></ProtectedRoute>} />
      <Route path="/survey/*" element={<ProtectedRoute requiredRole="admin"><AdminLayout /></ProtectedRoute>} />
      
      {/* Tenant routes */}
      <Route path="/:tenantSlug/auth" element={<AuthPage portal="player" />} />
      <Route path="/:tenantSlug/admin/auth" element={<AuthPage portal="admin" />} />
      <Route path="/:tenantSlug/deposit-return" element={<DepositReturnPage />} />
      <Route path="/:tenantSlug/download-app" element={<AndroidAppDownloadPage />} />
      <Route path="/:tenantSlug/app" element={<ProtectedRoute playerOnly><PlayerApp /></ProtectedRoute>} />
      <Route path="/:tenantSlug/app/fifa-kit" element={<PlayerHomeRedirect />} />
      <Route path="/:tenantSlug/app/explore" element={<ProtectedRoute playerOnly><ExploreEarnPage /></ProtectedRoute>} />
      <Route path="/:tenantSlug/app/explore/promo-code" element={<ProtectedRoute playerOnly><PromoCodePage /></ProtectedRoute>} />
      <Route path="/:tenantSlug/app/explore/vip-rewards" element={<ProtectedRoute playerOnly><VipRewardsPage /></ProtectedRoute>} />
      <Route path="/:tenantSlug/app/explore/marketplace" element={<ProtectedRoute playerOnly><ExploreSubPage section="marketplace" /></ProtectedRoute>} />
      <Route path="/:tenantSlug/app/explore/tiers" element={<ProtectedRoute playerOnly><ExploreSubPage section="tiers" /></ProtectedRoute>} />
      <Route path="/:tenantSlug/app/explore/daily-tasks" element={<ProtectedRoute playerOnly><TasksPage kind="daily" /></ProtectedRoute>} />
      <Route path="/:tenantSlug/app/explore/weekly-tasks" element={<ProtectedRoute playerOnly><TasksPage kind="weekly" /></ProtectedRoute>} />
      <Route path="/:tenantSlug/app/explore/referral" element={<ProtectedRoute playerOnly><ReferralPage /></ProtectedRoute>} />
      <Route path="/:tenantSlug/app/explore/roulette" element={<ProtectedRoute playerOnly><RoulettePage /></ProtectedRoute>} />
      <Route path="/:tenantSlug/app/halloween" element={<ProtectedRoute playerOnly><HalloweenEventPage /></ProtectedRoute>} />
      <Route path="/:tenantSlug/app/explore/predict-score" element={<PlayerHomeRedirect />} />
      <Route path="/:tenantSlug/app/brand" element={<ProtectedRoute playerOnly><BrandProfilePage /></ProtectedRoute>} />
      <Route path="/:tenantSlug/admin" element={<ProtectedRoute requiredRole="admin"><AdminLayout /></ProtectedRoute>} />
      <Route path="/:tenantSlug/admin/*" element={<ProtectedRoute requiredRole="admin"><AdminLayout /></ProtectedRoute>} />
      
      {/* Bare tenant URL: /morpheus → redirect to /morpheus/auth */}
      <Route path="/:tenantSlug" element={<TenantRootRedirect />} />
      
      {/* Root */}
      <Route path="/" element={<RootEntry />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <TenantThemeProvider>
            <TenantManifestLink />
            <AppRouter />
            <InstallPrompt />
            <AppToaster />
          </TenantThemeProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
